// src/pages/MyAccount.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../utils/firebaseconfig";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  addDoc,
  setDoc,
  getDocFromCache,
  increment
} from "firebase/firestore";
import ProductCard from "../components/ProductCard";
import "../styles/profile.css";

function MyAccount() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [viewingUserId, setViewingUserId] = useState(null);
  const profileUserId = location.state?.profileUserId;
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userProducts, setUserProducts] = useState([]);
  const [likedProducts, setLikedProducts] = useState([]);
  const [followersCount, setFollowersCount] = useState(0);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showAuctions, setShowAuctions] = useState(false);
  const [showLikes, setShowLikes] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProductForAuction, setSelectedProductForAuction] = useState(null);
  const [auctionForm, setAuctionForm] = useState({ startDate: "", endDate: "", startingBid: "" });

  useEffect(() => {
    onAuthStateChanged(auth, async (currentUserAuth) => {
      if (!currentUserAuth) {
        navigate("/login");
        return;
      }

      setCurrentUser(currentUserAuth);
      const targetUserId = profileUserId || currentUserAuth.uid;
      setIsOwnProfile(targetUserId === currentUserAuth.uid);

      const userDoc = await getDoc(doc(db, "User", targetUserId));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }

      const followersSnapshot = await getDocs(collection(db, "User", targetUserId, "Followers"));
      setFollowersCount(followersSnapshot.size);
      followersSnapshot.forEach((followerDoc) => {
        if (followerDoc.id === currentUserAuth.uid) {
          setIsFollowing(true);
        }
      });

      const productsSnapshot = await getDocs(collection(db, "Product"));
      const products = [];
      productsSnapshot.forEach((docSnap) => {
        if (docSnap.data().ownerID === targetUserId) {
          products.push({ id: docSnap.id, ...docSnap.data() });
        }
      });
      setUserProducts(products);

      const liked = [];
      for (let productDoc of productsSnapshot.docs) {
        const likeDoc = await getDoc(doc(db, "Product", productDoc.id, "Likes", currentUserAuth.uid));
        if (likeDoc.exists()) {
          liked.push({ id: productDoc.id, ...productDoc.data() });
        }
      }
      setLikedProducts(liked);
    });
  }, [navigate, profileUserId]);

  const handleFollowToggle = async () => {
    if (!currentUser || !profileUserId) return;
  
    const followerRef = doc(db, "User", profileUserId, "Followers", currentUser.uid);
    const userRef = doc(db, "User", profileUserId); // ✅ Fix: define this
  
    try {
      if (isFollowing) {
        await deleteDoc(followerRef);
        await updateDoc(userRef, {
          followersCount: Math.max((userData?.followersCount || 1) - 1, 0),
        });
        setFollowersCount((prev) => prev - 1);
      } else {
        await setDoc(followerRef, { followedAt: new Date().toISOString() });
        await updateDoc(userRef, {
          followersCount: (userData?.followersCount || 0) + 1,
        });
        setFollowersCount((prev) => prev + 1);
      }
  
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.error("Error toggling follow status:", error);
    }
  };

  const handleMoveToAuction = (product) => {
    setSelectedProductForAuction(product);
    setShowPopup(true);
  };

  const handleAuctionFormChange = (e) => {
    setAuctionForm({ ...auctionForm, [e.target.name]: e.target.value });
  };

  const handleAuctionSubmit = async () => {
    try {
      const auctionData = {
        ...auctionForm,
        productId: selectedProductForAuction.id,
        ownerID: currentUser.uid,
        title: selectedProductForAuction.title,
        image: selectedProductForAuction.images?.[0] || "",
        currentBid: auctionForm.startingBid,
        auctioneer: userData?.username || "Unknown",
        createdAt: new Date(),
        isAuctionActive: true
      };

      const docRef = await addDoc(collection(db, "Auction"), auctionData);

      await updateDoc(doc(db, "Product", selectedProductForAuction.id), {
        isAuction: true,
        relatedAuction: docRef.id
      });

      const updatedProducts = userProducts.map((p) =>
        p.id === selectedProductForAuction.id ? { ...p, isAuction: true, relatedAuction: docRef.id } : p
      );
      setUserProducts(updatedProducts);

      setShowPopup(false);
      setAuctionForm({ startDate: "", endDate: "", startingBid: "" });
      setSelectedProductForAuction(null);
    } catch (error) {
      console.error("Error moving to auction:", error);
    }
  };


  return (
    <main className="profile">
      <section className="profile-info">
        <figure className="profile-photo">
          <img src={userData?.profilePic || "https://placehold.co/150"} alt="User profile" width="150px" />
        </figure>
        <div className="profile-data-header">
          <h1 className="profile-name">{userData?.username}</h1>
          {isOwnProfile ? (
            <button className="profile-edit-button" onClick={() => navigate("/EditProfile")}>Edit profile</button>
          ) : (
            <button className="profile-edit-button" onClick={handleFollowToggle}>{isFollowing ? "Unfollow" : "Follow"}</button>
          )}
        </div>
        <div className="profile-follow-dataContainer">
          <div className="profile-follow-data">
            <p className="publications"><strong>{userProducts.length}</strong> posts</p>
            <p className="followers"><strong>{followersCount}</strong> Followers</p>
            <p className="following"><strong>{userData?.followingCount || 0}</strong> Following</p>
          </div>
        </div>
        <div className="profile-description">
          <p className="myaccount-bio">{userData?.bio}</p>
          <a href={`mailto:${userData?.contact?.email}`}> Email: {userData?.contact?.email}</a>
        </div>
      </section>

      <section className="button-option-container">
  <div>
    <button
      className={`view-product ${!showAuctions && !showLikes ? "active" : ""}`}
      onClick={() => {
        setShowAuctions(false);
        setShowLikes(false);
      }}
    >
      View Products
    </button>

    <button
      className={`view-auction ${showAuctions ? "active" : ""}`}
      onClick={() => {
        setShowAuctions(true);
        setShowLikes(false);
      }}
    >
      View Auctions
    </button>

    {isOwnProfile && (
      <button
        className={`view-likes ${showLikes ? "active" : ""}`}
        onClick={() => {
          setShowLikes(true);
          setShowAuctions(false);
        }}
      >
        My Likes
      </button>
    )}
  </div>
</section>

      <section className="profile-publications product-grid">
        {(showLikes ? likedProducts : userProducts.filter((p) => showAuctions ? p.isAuction : !p.isAuction)).map((product) => (
          <div key={product.id} className="product-card">
            <img src={product.images?.[0] || "https://placehold.co/300"} alt={product.name} className="product-card__image" onClick={() => navigate(`/product/${product.id}`, { state: { product } })} />
            <div className="product-card__details">
              <h3 className="product-card__title">{product.title}</h3>
              <p className="product-card__price">AED {product.price}</p>
              <p className="product-card__rating">Rating: {product.rating || 0}⭐</p>
              {isOwnProfile && !showLikes && (
                <div className="product-card__actions">
                  <button className="edit-btn" onClick={() => handleEditProduct(product)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDeleteProduct(product.id)}>Delete</button>
                  {!product.isAuction && (
                    <button className="auction-btn" onClick={() => handleMoveToAuction(product)}>Move to Auction</button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      {showPopup && selectedProductForAuction && (
        <div className="popup-overlay">
          <div className="popup-window styled-popup">
            <h3>Move to Auction</h3>
            <p><strong>{selectedProductForAuction.title}</strong></p>

            <label>Start Date:</label>
            <input type="datetime-local" name="startDate" value={auctionForm.startDate} onChange={handleAuctionFormChange} />

            <label>End Date:</label>
            <input type="datetime-local" name="endDate" value={auctionForm.endDate} onChange={handleAuctionFormChange} />

            <label>Starting Bid (AED):</label>
            <input type="number" name="startingBid" value={auctionForm.startingBid} onChange={handleAuctionFormChange} />

            <div className="popup-actions">
              <button className="submit-btn" onClick={handleAuctionSubmit}>Create Auction</button>
              <button className="cancel-btn" onClick={() => setShowPopup(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default MyAccount;
